import React, { useCallback } from 'react';
import { noop } from 'lodash';

import useInputHelpers from 'hooks/useInputHelpers.hook';
import fromAnyToString from 'utils/converters/fromAnyToString';
import withOptions from 'utils/functions/withOptions.util';
import logger from 'utils/logger.util';
import parseMoney from 'utils/parsers/parseMoney.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import asInput from 'utils/strings/asInput.util';

import CN from './MoneyInput.module.scss';

const CTAG = 'MoneyInput';
const L = logger(CTAG);

const RENDER_LOG = false;

const MoneyInput = ({
  children, // removed from extra, so it is not passed to the <input>
  displayDecimals,
  name,
  noNegative,
  onChange = noop,
  onBlur = noop,
  onFocus = noop,
  onClick = noop,
  readOnly: isReadOnly,
  tag: tagFromProps,
  value: valueFromProps,
  ...extra
}) => {
  const parser = withOptions((options) => parseMoney({ ...options, displayDecimals, noNegative }));

  const { attributes, value, focus, handle, is: input } = useInputHelpers(parser, valueFromProps, name, tagFromProps);
  const { received: receivedFocus, lost: lostFocus } = focus;
  const isDefocused = !input.focus;

  const handleChange = useCallback((ev) => handle(`${CTAG}.onChange`, onChange, ev), [handle, onChange]);
  const handleClick = useCallback((ev) => handle(`${CTAG}.onClick`, onClick, ev), [handle, onClick]);

  const handleFocus = useCallback(
    (ev) => {
      receivedFocus();
      return handle(`${CTAG}.onFocus`, onFocus, ev);
    },
    [receivedFocus, handle, onFocus],
  );

  const handleBlur = useCallback(
    (ev) => {
      lostFocus();
      return handle(`${CTAG}.onBlur`, onBlur, ev);
    },
    [handle, lostFocus, onBlur],
  );

  const { is, number, string, coin } = parseMoney({ value: valueFromProps, displayDecimals, noNegative });

  // since you can't change it, pretty print it during render, don't wait on blur event
  const displayValue = !isMissing(valueFromProps) && (isReadOnly || isDefocused) ? coin.str : value;

  if (RENDER_LOG) {
    L.debug('()', { valueFromProps, displayValue, value, isDefocused, isReadOnly, coin });
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      {...extra}
      {...attributes}
      data-bem="MoneyInput"
      data-is-valid={is.valid}
      data-is-empty={is.empty}
      data-is-int={is.int}
      data-is-float={is.float}
      data-is-positive={is.positive}
      data-is-negative={is.negative}
      data-is-nan={is.nan}
      data-is-infinite={is.infinite}
      data-is-rounded={is.rounded}
      data-as-number={fromAnyToString(number)}
      data-as-string={string}
      data-as-coin-str={coin.str}
      className={CN.component}
      name={name}
      value={asInput(displayValue)}
      onBlur={handleBlur}
      onChange={handleChange}
      onClick={handleClick}
      onFocus={handleFocus}
      readOnly={isReadOnly}
    />
  );
};

// noinspection JSUnusedGlobalSymbols
export default MoneyInput;
